import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaSearch,
  FaRegFileAlt,
  FaBuilding,
  FaTimes,
  FaExclamationCircle,
  FaCheckSquare,
  FaUserPlus,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { getAccessToken } from "../../storage/storage";
import axios from "axios";
import { FormatDate } from "../../utils/FormateDate";
import { TableSkelton } from "../../components/Skeletons";
import { MdOutlineRemoveRedEye } from "react-icons/md";
import AssignCleanerModal from "./components/AssignCleanerModal";

const FILTERS_STORAGE_KEY = "upcomingStaysFilters";
const PROPERTY_COLORS = {
  Pink: "#FF69B4",
  Teal: "#008080",
  Blue: "#0000FF",
  Green: "#008000",
  Purple: "#800080",
  Orange: "#FFA500",
  Red: "#FF0000",
  Yellow: "#FFD700",
};

const readPersistedFilters = () => {
  try {
    return JSON.parse(sessionStorage.getItem(FILTERS_STORAGE_KEY)) || {};
  } catch {
    return {};
  }
};

const propertyKey = (stay) =>
  stay?.property_details?.id ??
  stay?.property_id ??
  stay?.property ??
  stay?.property_name ??
  null;

// A stay is in the "declined" UX state when the cleaner column is empty
// (no current cleaner assigned) AND the backend still has a record of who
// last declined it. The backend clears `last_declined_by_name` automatically
// when the stay is reassigned.
const isStayDeclined = (stay) =>
  !stay?.cleaner_name && !!stay?.last_declined_by_name;

const parseStayIdsCsv = (csv) => {
  if (!csv) return [];
  return String(csv)
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => {
      const n = parseInt(s, 10);
      return Number.isNaN(n) ? s : n;
    });
};

const stayIsSelectable = (stay) => {
  const status = String(stay?.status || "").toUpperCase();
  // Terminal states cannot be reassigned. ACCEPTED *can* be reassigned
  // — the bulk-assign endpoint forcibly reassigns; the modal warns the user.
  if (status === "CANCELLED" || status === "COMPLETED") return false;
  return true;
};

const getLodgifyStatusBadgeColor = (status) => {
  switch (status?.toLowerCase()) {
    case "booked":
      return "bg-green-100 text-green-800";
    case "open":
      return "bg-yellow-100 text-yellow-800";
    case "declined":
      return "bg-red-100 text-red-800";
    case "tentative":
      return "bg-gray-100 text-gray-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

export default function UpcomingStays() {
  const token = getAccessToken();
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const navigate = useNavigate();
  const persisted = readPersistedFilters();

  const [search, setSearch] = useState("");
  const [stays, setStays] = useState([]);
  const [ownerProperties, setOwnerProperties] = useState([]);
  const [unassignedGuestStays, setUnassignedGuestStays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isCancelledStays, setIsCancelledStays] = useState(false);
  const [propertyFilter, setPropertyFilter] = useState(
    persisted.propertyFilter ?? "all"
  );
  const [showUnassignedOnly, setShowUnassignedOnly] = useState(
    !!persisted.showUnassignedOnly
  );

  const [selectedStayIds, setSelectedStayIds] = useState(() => new Set());
  const [assignModalOpen, setAssignModalOpen] = useState(false);

  const [banners, setBanners] = useState([]);
  const [highlightedStayIds, setHighlightedStayIds] = useState(
    () => new Set()
  );

  useEffect(() => {
    sessionStorage.setItem(
      FILTERS_STORAGE_KEY,
      JSON.stringify({ propertyFilter, showUnassignedOnly })
    );
  }, [propertyFilter, showUnassignedOnly]);

  const fetchStays = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    params.append("cancelled", String(isCancelledStays));
    if (showUnassignedOnly) params.append("unassigned", "true");
    axios
      .get(`${BASE_URL}/api/stays/?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })
      .then((response) => {
        const list = Array.isArray(response.data)
          ? response.data
          : response.data?.results || [];
        setStays(list);
      })
      .catch((error) => {
        console.error("Error fetching stays:", error);
      })
      .finally(() => setLoading(false));
  }, [BASE_URL, token, isCancelledStays, showUnassignedOnly]);

  useEffect(() => {
    fetchStays();
  }, [fetchStays]);

  useEffect(() => {
    axios
      .get(`${BASE_URL}/api/unassigned-guest-stays/`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })
      .then((response) => setUnassignedGuestStays(response.data))
      .catch((error) => console.error("Error fetching options:", error));
  }, [BASE_URL, token]);

  useEffect(() => {
    axios
      .get(`${BASE_URL}/api/properties/`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })
      .then((response) => {
        const list = Array.isArray(response.data)
          ? response.data
          : response.data?.results || [];
        setOwnerProperties(list);
      })
      .catch((error) => console.error("Error fetching properties:", error));
  }, [BASE_URL, token]);

  // Append-only update: never drops banners the user is currently looking at.
  // Backend's banner endpoint returns the single most-recent unread; the list
  // endpoint returns all unread. We merge whichever we get into `banners`,
  // de-duplicating by id and preserving display order (oldest at top).
  const mergeBanners = useCallback((incoming) => {
    setBanners((prev) => {
      const haveIds = new Set(prev.map((b) => String(b.id)));
      const additions = incoming.filter(
        (b) => b && b.id != null && !haveIds.has(String(b.id))
      );
      if (additions.length === 0) return prev;
      return [...prev, ...additions];
    });
  }, []);

  const fetchUnreadBanners = useCallback(() => {
    return axios
      .get(`${BASE_URL}/api/owner/notifications/?unread=true`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data : r.data?.results || [];
        mergeBanners(list);
      })
      .catch(() => {
        // Silent failure — banner is non-critical.
      });
  }, [BASE_URL, token, mergeBanners]);

  const fetchLatestBanner = useCallback(() => {
    return axios
      .get(`${BASE_URL}/api/owner/notifications/banner/`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })
      .then((r) => {
        const payload = r.data;
        let notification = null;
        if (payload && typeof payload === "object" && "notification" in payload) {
          notification = payload.notification || null;
        } else if (payload && payload.id != null) {
          notification = payload;
        }
        if (notification) mergeBanners([notification]);
      })
      .catch(() => {});
  }, [BASE_URL, token, mergeBanners]);

  // On mount, pull the full unread list. Subsequent polls only need the
  // most-recent unread (cheap, server-side ordering already correct).
  useEffect(() => {
    fetchUnreadBanners();
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") fetchLatestBanner();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") fetchLatestBanner();
    }, 30000);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      clearInterval(interval);
    };
  }, [fetchUnreadBanners, fetchLatestBanner]);

  const markBannerRead = useCallback(
    (notificationId) => {
      if (notificationId == null) return Promise.resolve();
      return axios
        .post(
          `${BASE_URL}/api/owner/notifications/mark-read/`,
          { notification_ids: [notificationId] },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        )
        .catch(() => {
          // Even if mark-read fails, we'll still hide the banner locally so
          // the user isn't stuck with a stale one.
        });
    },
    [BASE_URL, token]
  );

  const propertyOptions = useMemo(() => {
    const list = ownerProperties.map((p) => ({
      id: String(p.id),
      name: p.name || p.property_name || `Property #${p.id}`,
    }));
    if (list.length === 0) {
      const map = new Map();
      for (const stay of stays) {
        const key = propertyKey(stay);
        if (key == null) continue;
        if (!map.has(String(key))) {
          map.set(String(key), { id: String(key), name: stay.property_name });
        }
      }
      return Array.from(map.values()).sort((a, b) =>
        (a.name || "").localeCompare(b.name || "")
      );
    }
    return list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [ownerProperties, stays]);

  useEffect(() => {
    if (
      propertyFilter !== "all" &&
      propertyOptions.length > 0 &&
      !propertyOptions.some((p) => p.id === String(propertyFilter))
    ) {
      setPropertyFilter("all");
    }
  }, [propertyOptions, propertyFilter]);

  const filteredStays = useMemo(() => {
    const query = search.toLowerCase().trim();
    return stays.filter((stay) => {
      if (query) {
        const matches =
          stay.property_name?.toLowerCase().includes(query) ||
          stay.cleaner_name?.toLowerCase().includes(query) ||
          String(stay.id)?.toLowerCase().includes(query);
        if (!matches) return false;
      }
      if (propertyFilter !== "all") {
        if (String(propertyKey(stay)) !== String(propertyFilter)) return false;
      }
      return true;
    });
  }, [stays, search, propertyFilter]);

  const bulkSelectEnabled = propertyFilter !== "all" && !isCancelledStays;

  // Bulk selection is scoped to a single property (a batch is single-property).
  // Clear any selection whenever the property filter changes — including
  // between two specific properties — so a selection can never span two
  // properties. Also clears when bulk select becomes unavailable ("All"
  // filter or the cancelled view). Guarded so an already-empty selection
  // doesn't trigger an extra render (e.g. on mount).
  useEffect(() => {
    setSelectedStayIds((prev) => (prev.size > 0 ? new Set() : prev));
  }, [propertyFilter, bulkSelectEnabled]);

  const visibleSelectableStays = useMemo(
    () => (bulkSelectEnabled ? filteredStays.filter(stayIsSelectable) : []),
    [bulkSelectEnabled, filteredStays]
  );

  const allVisibleSelected =
    visibleSelectableStays.length > 0 &&
    visibleSelectableStays.every((s) => selectedStayIds.has(s.id));

  const toggleStay = (id) => {
    setSelectedStayIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedStayIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        visibleSelectableStays.forEach((s) => next.delete(s.id));
      } else {
        visibleSelectableStays.forEach((s) => next.add(s.id));
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedStayIds(new Set());

  const selectedStays = useMemo(
    () => stays.filter((s) => selectedStayIds.has(s.id)),
    [stays, selectedStayIds]
  );

  const filteredPropertyName = useMemo(() => {
    if (propertyFilter === "all") return null;
    return (
      propertyOptions.find((p) => p.id === String(propertyFilter))?.name || null
    );
  }, [propertyFilter, propertyOptions]);

  const declinedStayIds = useMemo(() => {
    const ids = new Set();
    stays.forEach((s) => {
      if (isStayDeclined(s)) ids.add(s.id);
    });
    return ids;
  }, [stays]);

  // Scroll the first highlighted row into view once the table has rendered
  // it. We retry briefly because the row may not exist yet if stays/property
  // filter are still loading.
  useEffect(() => {
    if (highlightedStayIds.size === 0) return;
    let cancelled = false;
    const firstId = highlightedStayIds.values().next().value;
    let attempts = 0;
    const tryScroll = () => {
      if (cancelled) return;
      const el = document.querySelector(`[data-stay-id="${firstId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }
      if (attempts++ < 10) setTimeout(tryScroll, 150);
    };
    tryScroll();
    return () => {
      cancelled = true;
    };
  }, [highlightedStayIds]);

  const handleAddCleaner = (stay) => {
    sessionStorage.setItem("cleanerEdit", JSON.stringify(true));
    navigate("/stays/add-stays", { state: { stay } });
  };

  const handleTaskSheetClick = (id) => navigate(`/task-sheet/${id}`);
  const handleInvoiceClick = (id) => navigate(`/invoice/${id}`);

  const handleBannerDismiss = async (notification) => {
    if (!notification) return;
    // Remove from local list immediately so the UI feels responsive.
    setBanners((prev) =>
      prev.filter((b) => String(b.id) !== String(notification.id))
    );
    // Drop highlights this banner had set.
    const data = notification.data || {};
    const idsToDrop = parseStayIdsCsv(
      data.declined_stay_ids ?? data.stay_ids ?? notification.declined_stay_ids ?? ""
    );
    if (idsToDrop.length > 0) {
      setHighlightedStayIds((prev) => {
        const next = new Set(prev);
        idsToDrop.forEach((id) => next.delete(id));
        return next;
      });
    }
    if (notification.id != null) {
      await markBannerRead(notification.id);
      // After server-side mark-read, see if there's another unread to pull in.
      fetchLatestBanner();
    }
  };

  const handleBannerClick = (notification) => {
    if (!notification) return;
    const data = notification.data || {};
    const pid = data.property_id ?? data.property ?? notification.property_id ?? null;
    if (pid != null) {
      const key = String(pid);
      if (propertyOptions.some((p) => p.id === key)) {
        setPropertyFilter(key);
      }
    }
    const ids = parseStayIdsCsv(
      data.declined_stay_ids ?? data.stay_ids ?? notification.declined_stay_ids ?? ""
    );
    if (ids.length > 0) {
      // Replace highlights — last clicked banner takes focus. (Union would
      // confuse the user about which decline event they're acting on.)
      setHighlightedStayIds(new Set(ids));
    }
  };

  const handleAssigned = () => {
    // Clear any banner-driven highlights for stays we just touched.
    if (highlightedStayIds.size > 0) {
      setHighlightedStayIds((prev) => {
        const next = new Set(prev);
        selectedStayIds.forEach((id) => next.delete(id));
        return next;
      });
    }
    clearSelection();
    fetchStays();
    fetchLatestBanner();
  };

  // Manually changing the property filter invalidates the banner-driven
  // highlights (they were for a specific property's declined stays).
  const handlePropertyFilterChange = (value) => {
    setPropertyFilter(value);
    if (highlightedStayIds.size > 0) setHighlightedStayIds(new Set());
  };

  const selectedPropertyId = propertyFilter !== "all" ? propertyFilter : null;
  const selectedCount = selectedStayIds.size;

  return (
    <div className="flex lg:flex-row flex-col lg:items-start items-center px-6 w-full h-full pb-8 overflow-x-hidden">
      <div className="lg:px-7px-4 mt-8 mb-16 h-[100vh] w-full">
        <div className="flex justify-between w-[85%] items-center ">
          <h1 className="md:text-3xl text-lg font-bold text-[#586989] mb-8 ">
            Upcoming Stays
          </h1>
          <button
            onClick={() => {
              sessionStorage.removeItem("cleanerEdit");
              navigate("/stays/add-stays");
            }}
            className="bg-[#586989] text-white px-6 py-3 rounded-md md:flex items-center justify-center hidden"
          >
            + Add New Stay
          </button>
        </div>
        <p className="text-gray-600 pt-2">
          Manage guest stays and cleaning assignments
        </p>

        {banners.length > 0 && (
          <div className="flex flex-col gap-2 mt-4">
            {banners.map((n) => {
              const nd = n.data || {};
              // A declined/rejected notification → red; everything else
              // (accepted, generic) → green.
              const isDeclined =
                String(nd.action || "").toLowerCase() === "declined" ||
                String(nd.type || "").toLowerCase() === "cleaner_declined";
              const reason = (nd.reason || "").trim();
              return (
                <div
                  key={n.id}
                  className={`flex items-start gap-3 text-white rounded-md px-4 py-3 text-sm shadow-md ${
                    isDeclined ? "bg-red-700" : "bg-green-700"
                  }`}
                  role="alert"
                >
                  <FaExclamationCircle className="flex-shrink-0 text-base mt-0.5" />
                  <button
                    type="button"
                    onClick={() => handleBannerClick(n)}
                    className="text-left flex-1 hover:underline"
                  >
                    <span className="block">
                      {n.message ||
                        n.title ||
                        "You have a new notification — tap to view"}
                    </span>
                    {isDeclined && reason && (
                      <span className="block mt-1 italic text-white/90">
                        Reason: “{reason}”
                      </span>
                    )}
                  </button>
                  <button
                    type="button"
                    aria-label="Dismiss notification"
                    onClick={() => handleBannerDismiss(n)}
                    className="text-white/80 hover:text-white mt-0.5"
                  >
                    <FaTimes />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <div className="flex md:flex-row flex-col gap-2 mt-4 items-center">
          <div className="relative md:w-[40%] w-full">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
            <input
              className="w-full p-2 border outline-none h-[55px] px-4 rounded-lg pl-10"
              placeholder="Search by property, reference, or cleaner"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <button
            className={`px-6 py-3 rounded-md flex items-center justify-center h-[55px] border border-[#586989] transition-colors ${
              isCancelledStays
                ? "bg-[#586989] text-white"
                : "bg-transparent text-black"
            }`}
            onClick={() => setIsCancelledStays(!isCancelledStays)}
          >
            Cancelled Bookings
          </button>
        </div>

        <div className="flex md:flex-row flex-col gap-6 mt-4 md:items-center items-stretch bg-white rounded-lg border border-gray-200 px-5 py-3">
          <div className="flex items-center gap-3 md:flex-1 md:max-w-md">
            <FaBuilding className="text-[#586989] text-lg flex-shrink-0" />
            <select
              value={propertyFilter}
              onChange={(e) => handlePropertyFilterChange(e.target.value)}
              aria-label="Filter by property"
              className={`text-sm rounded-md border px-4 py-2.5 outline-none cursor-pointer flex-1 min-w-[260px] ${
                propertyFilter !== "all"
                  ? "border-[#586989] bg-[#eef1f6] text-[#586989] font-medium"
                  : "border-gray-300 text-gray-700"
              }`}
            >
              <option value="all">All Properties</option>
              {propertyOptions.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>

          <div className="h-6 w-px bg-gray-200 hidden md:block" />

          <button
            type="button"
            onClick={() => setShowUnassignedOnly((v) => !v)}
            aria-pressed={showUnassignedOnly}
            className={`flex items-center gap-2 text-xs rounded-full px-4 py-2 border transition-colors whitespace-nowrap ${
              showUnassignedOnly
                ? "border-[#586989] bg-[#eef1f6] text-[#586989] font-semibold"
                : "border-gray-300 text-[#586989] bg-white"
            }`}
          >
            <span
              className={`inline-block w-2 h-2 rounded-full ${
                showUnassignedOnly ? "bg-[#586989]" : "bg-gray-300"
              }`}
            />
            {showUnassignedOnly ? "Showing unassigned only" : "Show unassigned only"}
          </button>

          <div className="md:ml-auto text-xs text-gray-500 whitespace-nowrap">
            Showing {filteredStays.length} of {stays.length} stays
            {!bulkSelectEnabled && propertyFilter === "all" && stays.length > 0 && (
              <span className="ml-2 text-[#586989]">
                · Filter by a property to bulk-assign
              </span>
            )}
          </div>
        </div>

        {!isCancelledStays &&
          unassignedGuestStays?.unassigned_stays?.length > 0 &&
          unassignedGuestStays.unassigned_stays.map((stay) => (
            <div
              key={stay.id}
              className="flex md:flex-row flex-col gap-4 mt-4 bg-orange-100 border-l-4 border-orange-500 text-orange-700 p-4"
              role="alert"
            >
              <p className="font-bold">{stay.property_name}</p>
              <p>Stay reference {stay.id} requires to assign cleaner</p>
            </div>
          ))}

        <div className={`relative mt-4 ${selectedCount > 0 ? "pb-24" : "pb-5"}`}>
          <div className="border border-[#DFDFDF] bg-white rounded-lg shadow-md overflow-auto h-[70vh]">
            <table className="w-full border-collapse">
              <thead className="sticky top-0 bg-[#FFF5CE] text-[#586989] z-10">
                <tr>
                  {bulkSelectEnabled && (
                    <th className="px-5 py-4 text-center w-[64px]">
                      <input
                        type="checkbox"
                        aria-label="Select all visible stays"
                        checked={allVisibleSelected}
                        onChange={toggleSelectAll}
                        disabled={visibleSelectableStays.length === 0}
                        className="accent-[#586989] cursor-pointer w-4 h-4 align-middle"
                      />
                    </th>
                  )}
                  <th className="p-4 text-center min-w-[150px]">Stay ID</th>
                  <th className="p-2 text-center min-w-[150px]">Property Name</th>
                  <th className="p-2 text-center min-w-[150px]">Booking Status</th>
                  <th className="p-2 text-center min-w-[150px]">Check-in</th>
                  <th className="p-2 text-center min-w-[150px]">Check-out</th>
                  <th className="p-2 text-center min-w-[150px]">#Guests</th>
                  <th className="p-2 text-center min-w-[150px]">#Pets</th>
                  <th className="p-2 text-center min-w-[150px]">Cleaner</th>
                  <th className="p-2 text-center min-w-[150px]">Cleaner Status</th>
                  <th className="p-2 text-center min-w-[150px]">Invoice Status</th>
                  <th className="py-3 px-4 min-w-[150px] text-center">Action Date</th>
                  <th className="p-2 text-center min-w-[150px]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <TableSkelton />
                ) : filteredStays.length > 0 ? (
                  filteredStays.map((stay) => {
                    const selectable = stayIsSelectable(stay);
                    const isSelected = selectedStayIds.has(stay.id);
                    const isDeclined = declinedStayIds.has(stay.id);
                    const isHighlighted = highlightedStayIds.has(stay.id);
                    const rowBg = isHighlighted
                      ? "bg-yellow-100"
                      : isDeclined
                      ? "bg-red-50"
                      : isSelected
                      ? "bg-[#eef1f6]"
                      : "";
                    return (
                      <tr
                        key={stay.id}
                        data-stay-id={stay.id}
                        className={`border-b transition-colors ${rowBg}`}
                      >
                        {bulkSelectEnabled && (
                          <td className="px-5 py-4 text-center w-[64px]">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleStay(stay.id)}
                              disabled={!selectable}
                              aria-label={`Select stay ${stay.id}`}
                              className="accent-[#586989] cursor-pointer w-4 h-4 align-middle disabled:opacity-30 disabled:cursor-not-allowed"
                            />
                          </td>
                        )}
                        <td className="p-4 text-center whitespace-nowrap">
                          <div
                            className="px-3 py-1 rounded"
                            style={{
                              backgroundColor:
                                PROPERTY_COLORS[
                                  stay?.property_details?.property_color
                                ] || "#6B7280",
                              opacity: 0.6,
                            }}
                          >
                            <span
                              className="text-sm font-medium"
                              style={{ color: "white" }}
                            >
                              #{stay.id}
                            </span>
                          </div>
                        </td>
                        <td className="p-2 text-center whitespace-nowrap">
                          {stay.property_name}
                        </td>
                        <td className="p-2 text-center whitespace-nowrap">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full font-medium text-xs ${getLodgifyStatusBadgeColor(
                              stay.lodgify_status
                            )}`}
                          >
                            {stay.lodgify_status}
                          </span>
                        </td>
                        <td className="p-2 text-center whitespace-nowrap">
                          {FormatDate(stay.check_in_date)}
                        </td>
                        <td className="p-2 text-center whitespace-nowrap">
                          {FormatDate(stay.check_out_date)}
                        </td>
                        <td className="p-2 text-center whitespace-nowrap">
                          {stay.number_of_guests}
                        </td>
                        <td className="p-2 text-center whitespace-nowrap">
                          {stay.number_of_pets}
                        </td>
                        {stay.status !== "CANCELLED" ? (
                          <td className="p-2 text-center">
                            {stay?.cleaner_name ? (
                              <span className="inline-flex items-center gap-1.5 text-sm">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#586989]" />
                                {stay.cleaner_name}
                              </span>
                            ) : isDeclined ? (
                              <span className="inline-flex items-center gap-1 text-red-600 text-xs font-medium">
                                <FaTimes className="text-[10px]" />
                                {stay.last_declined_by_name} declined
                              </span>
                            ) : (
                              <button
                                onClick={() => handleAddCleaner(stay)}
                                className="bg-blue-500 text-white px-2 py-1 rounded"
                              >
                                Add Cleaner
                              </button>
                            )}
                          </td>
                        ) : (
                          <td className="p-2 text-center">-</td>
                        )}
                        <td className="p-2 text-center whitespace-nowrap">
                          {stay.status === "CANCELLED" ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-white bg-[#EF4444]">
                              {stay.status}
                            </span>
                          ) : isDeclined ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                              Unassigned
                            </span>
                          ) : (
                            stay.status || "-"
                          )}
                        </td>
                        <td className="p-2 text-center whitespace-nowrap">
                          {stay?.invoices?.[0]?.status
                            ? stay.invoices[0].status.toUpperCase()
                            : "-"}
                        </td>
                        <td className="px-4 py-6 text-center flex items-center justify-center">
                          {stay?.invoices?.[0]?.status === "approved" ||
                          stay?.invoices?.[0]?.status === "paid" ||
                          stay?.invoices?.[0]?.status === "rejected"
                            ? stay?.invoices?.[0]?.action_date
                              ? FormatDate(stay.invoices[0].action_date)
                              : "-"
                            : "-"}
                        </td>
                        {stay.status !== "CANCELLED" ? (
                          <td className="p-2 text-center whitespace-nowrap">
                            {isDeclined ? (
                              <button
                                onClick={() => handleAddCleaner(stay)}
                                className="bg-[#586989] text-white px-3 py-1 rounded text-xs font-semibold"
                              >
                                Reassign
                              </button>
                            ) : stay.status === "ACCEPTED" ? (
                              <div className="text-green-700">
                                <span
                                  onClick={() => handleTaskSheetClick(stay.id)}
                                  className="cursor-pointer flex items-center gap-1 justify-center"
                                >
                                  <FaRegFileAlt /> View Task Sheet
                                </span>
                                <span
                                  onClick={() => handleInvoiceClick(stay.id)}
                                  className="cursor-pointer flex items-center gap-1 justify-center"
                                >
                                  <FaRegFileAlt /> View Invoice
                                </span>
                                <div
                                  className="flex items-center justify-center cursor-pointer"
                                  onClick={() => handleAddCleaner(stay)}
                                >
                                  <MdOutlineRemoveRedEye />
                                </div>
                              </div>
                            ) : (
                              <div
                                className="flex items-center justify-center cursor-pointer"
                                onClick={() => handleAddCleaner(stay)}
                              >
                                <MdOutlineRemoveRedEye />
                              </div>
                            )}
                          </td>
                        ) : (
                          <td className="p-2 text-center">-</td>
                        )}
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan={bulkSelectEnabled ? 13 : 12}
                      className="text-center text-lg font-bold text-[#586989] h-[400px]"
                    >
                      {stays.length === 0
                        ? "No Upcoming stays"
                        : "No stays match the current filters"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {selectedCount > 0 && (
            <div
              className="fixed bottom-6 left-4 right-4 lg:left-[calc(16rem+1.5rem)] lg:right-6 z-40 rounded-xl shadow-2xl text-white px-6 py-4 flex items-center gap-4"
              style={{ background: "#586989" }}
              role="region"
              aria-label="Bulk selection actions"
            >
              <FaCheckSquare className="text-lg flex-shrink-0" />
              <span className="text-sm">
                <span className="font-bold text-lg mr-1.5">{selectedCount}</span>
                stay{selectedCount === 1 ? "" : "s"} selected
              </span>
              <button
                type="button"
                onClick={() => setAssignModalOpen(true)}
                className="ml-auto px-5 py-2.5 rounded-md text-sm font-bold flex items-center gap-2"
                style={{ background: "#FFF5CE", color: "#586989" }}
              >
                <FaUserPlus />
                Assign Cleaner
              </button>
              <button
                type="button"
                onClick={clearSelection}
                className="text-xs underline text-[#BCC8D8] hover:text-white whitespace-nowrap"
              >
                Clear selection
              </button>
            </div>
          )}
        </div>
      </div>

      <AssignCleanerModal
        open={assignModalOpen}
        onClose={() => setAssignModalOpen(false)}
        selectedStays={selectedStays}
        propertyId={selectedPropertyId}
        propertyName={filteredPropertyName}
        onAssigned={handleAssigned}
      />
    </div>
  );
}
